import type { GameSession, SessionProgram, SessionProgramSlot } from '@business-game/shared';

export type NormalizedSession = {
  session: GameSession;
  anchorDate: Date;
  players: number;
  completed: number;
};

export type MonthStatsEntry = {
  key: string;
  label: string;
  sessions: number;
  players: number;
  completed: number;
  archived: number;
  timestamp: number;
};

export type MonthlySeriesRange =
  | { type: 'last'; count: number }
  | { type: 'between'; from: Date; to: Date }
  | { type: 'all' };

export const getSessionAnchorDate = (session: GameSession): Date =>
  new Date(session.startDate ?? session.createdAt ?? new Date());

export const getNumber = (value: unknown): number => (typeof value === 'number' ? value : 0);

export const monthLabel = (date: Date): string =>
  date.toLocaleDateString('fr-FR', {
    month: 'short',
    year: 'numeric',
  });

export const monthKeyFromDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export const parseMonthKey = (key: string): Date => {
  const [year, month] = key.split('-').map(Number);
  return new Date(year, month - 1, 1);
};

export const normalizeSessions = (sessions: GameSession[]): NormalizedSession[] =>
  sessions.map((session) => ({
    session,
    anchorDate: getSessionAnchorDate(session),
    players: getNumber((session as GameSession & { players?: number }).players),
    completed: getNumber((session as GameSession & { completed?: number }).completed),
  }));

export const filterPlayedSessions = (sessions: NormalizedSession[]): NormalizedSession[] =>
  sessions.filter((item) => item.players > 0);

export type PlannedSlotEntry = {
  slot: SessionProgramSlot;
  programName: string;
  scheduledAt: Date;
};

export const filterScheduledSessions = (sessions: NormalizedSession[]): NormalizedSession[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return sessions.filter((item) => {
    if (item.players > 0 || item.session.archivedAt) {
      return false;
    }

    const scheduleDate = item.session.startDate ? new Date(item.session.startDate) : null;
    if (!scheduleDate) {
      return false;
    }

    scheduleDate.setHours(0, 0, 0, 0);
    return scheduleDate.getTime() >= today.getTime();
  });
};

export const extractPlannedSlots = (programs: SessionProgram[]): PlannedSlotEntry[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const entries: PlannedSlotEntry[] = [];

  programs.forEach((program) => {
    program.slots.forEach((slot) => {
      if (slot.sessionId) {
        return;
      }

      const scheduledAt = new Date(slot.scheduledAt);
      scheduledAt.setHours(0, 0, 0, 0);
      if (scheduledAt.getTime() >= today.getTime()) {
        entries.push({ slot, programName: program.name, scheduledAt });
      }
    });
  });

  return entries;
};

const emptyMonthEntry = (monthStart: Date): MonthStatsEntry => ({
  key: monthKeyFromDate(monthStart),
  label: monthLabel(monthStart),
  sessions: 0,
  players: 0,
  completed: 0,
  archived: 0,
  timestamp: monthStart.getTime(),
});

export const buildMonthStatsMap = (playedSessions: NormalizedSession[]): Map<string, MonthStatsEntry> => {
  const monthMap = new Map<string, MonthStatsEntry>();

  playedSessions.forEach(({ session, anchorDate, players, completed }) => {
    const key = monthKeyFromDate(anchorDate);
    const timestamp = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1).getTime();
    const existing = monthMap.get(key) ?? {
      key,
      label: monthLabel(anchorDate),
      sessions: 0,
      players: 0,
      completed: 0,
      archived: 0,
      timestamp,
    };

    existing.sessions += 1;
    existing.players += players;
    existing.completed += completed;
    if (session.archivedAt) {
      existing.archived += 1;
    }

    monthMap.set(key, existing);
  });

  return monthMap;
};

export const getEarliestPlayedMonth = (playedSessions: NormalizedSession[]): Date | null => {
  if (playedSessions.length === 0) {
    return null;
  }

  const earliest = playedSessions.reduce((min, item) =>
    item.anchorDate.getTime() < min.getTime() ? item.anchorDate : min
  , playedSessions[0].anchorDate);

  return new Date(earliest.getFullYear(), earliest.getMonth(), 1);
};

export const buildMonthlySeries = (
  playedSessions: NormalizedSession[],
  range: MonthlySeriesRange
): MonthStatsEntry[] => {
  const monthMap = buildMonthStatsMap(playedSessions);
  const now = new Date();
  const endMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  let startMonth: Date;
  if (range.type === 'last') {
    startMonth = new Date(now.getFullYear(), now.getMonth() - (range.count - 1), 1);
  } else if (range.type === 'between') {
    startMonth = new Date(range.from.getFullYear(), range.from.getMonth(), 1);
    const toMonth = new Date(range.to.getFullYear(), range.to.getMonth(), 1);
    if (startMonth.getTime() > toMonth.getTime()) {
      return [];
    }
    const months: MonthStatsEntry[] = [];
    const cursor = new Date(startMonth);
    while (cursor.getTime() <= toMonth.getTime()) {
      const key = monthKeyFromDate(cursor);
      months.push(monthMap.get(key) ?? emptyMonthEntry(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return months;
  } else {
    startMonth = getEarliestPlayedMonth(playedSessions) ?? endMonth;
  }

  const months: MonthStatsEntry[] = [];
  const cursor = new Date(startMonth);
  while (cursor.getTime() <= endMonth.getTime()) {
    const key = monthKeyFromDate(cursor);
    months.push(monthMap.get(key) ?? emptyMonthEntry(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return months;
};

export const summarizePeriod = (entries: MonthStatsEntry[]) => ({
  sessions: entries.reduce((acc, item) => acc + item.sessions, 0),
  players: entries.reduce((acc, item) => acc + item.players, 0),
  completed: entries.reduce((acc, item) => acc + item.completed, 0),
  archived: entries.reduce((acc, item) => acc + item.archived, 0),
  months: entries.length,
  activeMonths: entries.filter((item) => item.sessions > 0).length,
});

const CSV_HEADERS = [
  'date',
  'code_session',
  'nom_session',
  'mode',
  'statut',
  'joueurs',
  'equipes_terminees',
  'archivee',
  'raison_archive',
  'langue',
  'timezone',
  'programme',
  'facilitateur',
];

const sessionStatusLabel = (session: GameSession): string => {
  if (session.archivedAt) return 'archivee';
  if (session.isClosed) return 'fermee';
  return 'ouverte';
};

export const buildSessionsCsv = (rows: NormalizedSession[]): string =>
  [
    CSV_HEADERS.join(','),
    ...rows.map(({ session, anchorDate, players, completed }) =>
      [
        anchorDate.toISOString(),
        session.code,
        session.name ?? '',
        session.gameMode,
        sessionStatusLabel(session),
        players,
        completed,
        session.archivedAt ? 'oui' : 'non',
        session.archivedReason ?? '',
        session.language ?? '',
        session.timezone ?? '',
        session.programName ?? '',
        session.notes ?? '',
      ]
        .map((value) => `"${String(value).replace(/"/g, '""')}"`)
        .join(',')
    ),
  ].join('\n');

export const downloadCsv = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const filterSessionsByMonthKey = (
  playedSessions: NormalizedSession[],
  monthKey: string
): NormalizedSession[] =>
  playedSessions
    .filter(({ anchorDate }) => monthKeyFromDate(anchorDate) === monthKey)
    .sort((a, b) => a.anchorDate.getTime() - b.anchorDate.getTime());

export const filterSessionsInMonthRange = (
  playedSessions: NormalizedSession[],
  fromKey: string,
  toKey: string
): NormalizedSession[] => {
  const fromTime = parseMonthKey(fromKey).getTime();
  const toTime = parseMonthKey(toKey).getTime();
  const [startTime, endTime] = fromTime <= toTime ? [fromTime, toTime] : [toTime, fromTime];

  return playedSessions
    .filter(({ anchorDate }) => {
      const monthTime = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1).getTime();
      return monthTime >= startTime && monthTime <= endTime;
    })
    .sort((a, b) => a.anchorDate.getTime() - b.anchorDate.getTime());
};

export const exportMonthCsv = (playedSessions: NormalizedSession[], monthKey: string) => {
  const rows = filterSessionsByMonthKey(playedSessions, monthKey);
  if (rows.length === 0) {
    return;
  }
  downloadCsv(buildSessionsCsv(rows), `sessions-jouees-${monthKey}.csv`);
};

export const exportPeriodCsv = (
  playedSessions: NormalizedSession[],
  fromKey: string,
  toKey: string
) => {
  const rows = filterSessionsInMonthRange(playedSessions, fromKey, toKey);
  if (rows.length === 0) {
    return;
  }
  downloadCsv(buildSessionsCsv(rows), `sessions-jouees-${fromKey}_${toKey}.csv`);
};
